import { IEConsentForm } from '@/api/dtos';
import EConsentContext, { EConsentUserType } from '@/contexts/EConsentContext';
import { TextField, Typography } from '@mui/material';
import { DatePicker, FormContext, GroupedField, IFormContext } from '@ngt/opms';
import { FC, ReactNode, useContext, useMemo } from 'react';
import InputDisplay from '@/components/forms/inputs/InputDisplay';
import EConsentChecklist from './EConsentChecklist';

const sectionGrid = { size: { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 } };
const containerGrid = { sx: { paddingBottom: 1.5, paddingTop: 1.5 } };

export interface EConsentSignatureFieldsProps {
    investigatorStatement?: ReactNode;
}

const EConsentSignatureFields: FC<EConsentSignatureFieldsProps> = ({ investigatorStatement }) => {
    const { investigators, participantName, userType } = useContext(EConsentContext);
    const { data: form } = useContext(FormContext) as unknown as IFormContext<IEConsentForm>;
    const investigatorName = useMemo(
        () => form?.assignedInvestigatorUsername
            ? investigators?.[form.assignedInvestigatorUsername]
            : undefined,
        [form?.assignedInvestigatorUsername, investigators]
    );

    return (
        <>
            <Typography sx={{ pt: 2, pb: 1.5, fontWeight: 'bold' }}>
                Person taking part in the trial
            </Typography>
            <GroupedField
                propertyPath="patientName"
                component={InputDisplay}
                valueConvert={() => <TextField value={participantName?.toUpperCase() ?? ''} disabled fullWidth />}
                FieldGridProps={sectionGrid}
                ContainerGridProps={containerGrid}
            />
            <GroupedField
                propertyPath="datePatientSignedConsent"
                component={DatePicker}
                FieldGridProps={sectionGrid}
                ContainerGridProps={containerGrid}
                disabled
            />

            {userType === EConsentUserType.Investigator && (
                <>
                    <Typography sx={{ pt: 2, pb: 1.5, fontWeight: 'bold' }}>
                        Person conducting the informed consent discussion (Principal Investigator or Senior Researcher)
                    </Typography>
                    {investigatorStatement && <EConsentChecklist items={[investigatorStatement]} />}
                    <GroupedField
                        propertyPath="investigatorName"
                        component={InputDisplay}
                        valueConvert={() => <TextField value={investigatorName?.toUpperCase() ?? ''} disabled fullWidth />}
                        FieldGridProps={sectionGrid}
                        ContainerGridProps={containerGrid}
                    />
                    <GroupedField
                        propertyPath="dateInvestigatorSignedConsent"
                        component={DatePicker}
                        disabled
                        FieldGridProps={sectionGrid}
                        ContainerGridProps={containerGrid}
                    />
                </>
            )}
        </>
    );
};

export default EConsentSignatureFields;
